import React, { memo, useContext } from 'react';
import { Handle, Position } from 'reactflow';
import {
  FaDatabase,
  FaPlay,
  FaEdit,
  FaPlus,
  FaTrash,
  FaQuestion,
  FaStop,
  FaFileImport,
  FaFileExport,
  FaCode,
  FaEnvelope,
  FaSearch,
  FaTable,
} from 'react-icons/fa';
import { MdLoop } from 'react-icons/md';
import { BiGitBranch } from 'react-icons/bi';
import { VscVariableGroup } from 'react-icons/vsc';
import { AiOutlineFunction } from 'react-icons/ai';
import { NodeDisplayContext } from './NodeDisplayContext';
import { colorForNode, isConnectorStep } from './connectorColor';
import './StepNode.css';

const iconMap = {
  start: <FaPlay />,
  executeRunbook: <FaPlay />,
  query: <FaDatabase />,
  lookup: <FaSearch />,
  create: <FaPlus />,
  update: <FaEdit />,
  delete: <FaTrash />,
  ifElse: <BiGitBranch />,
  forEach: <MdLoop />,
  setVar: <FaCode />,
  jsonSetVar: <FaCode />,
  getPushedData: <FaFileImport />,
  executeStoredProcedure: <AiOutlineFunction />,
  declareVar: <VscVariableGroup />,
  logMessage: <FaFileExport />,
  setError: <FaStop />,
  endLoop: <FaStop />,
  sendEmail: <FaEnvelope />,
  upsertClientVar: <VscVariableGroup />,
  buildTableCache: <FaTable />,
  lookupTableCache: <FaTable />,
  queryTableCache: <FaTable />,
  deleteTableCache: <FaTable />,
};

// Short action verb shown under the icon.
const verbMap = {
  start: 'Start',
  executeRunbook: 'Run',
  query: 'Query',
  lookup: 'Lookup',
  create: 'Create',
  update: 'Update',
  delete: 'Delete',
  ifElse: 'If/Else',
  forEach: 'For Each',
  setVar: 'Set Var',
  jsonSetVar: 'Set Var',
  getPushedData: 'Get Data',
  executeStoredProcedure: 'Sproc',
  declareVar: 'Declare',
  logMessage: 'Log',
  setError: 'Error',
  endLoop: 'End Loop',
  sendEmail: 'Email',
  upsertClientVar: 'Upsert Var',
  buildTableCache: 'Build Cache',
  lookupTableCache: 'Lookup Cache',
  queryTableCache: 'Query Cache',
  deleteTableCache: 'Delete Cache',
};

const DESC_MAX = 30;

function truncate(text, max) {
  if (!text) return '';
  return text.length > max ? `${text.slice(0, max).trimEnd()}…` : text;
}

// Resolve the forEach loop description from its upstream data step.
function resolveDescription(data, allNodes, edges, id) {
  let displayDescription = data.description;
  if (data.stepType === 'forEach' && allNodes && edges) {
    let dataStepId = data.dataStepId;
    if (!dataStepId) {
      const incomingEdges = edges.filter((e) => e.target === id);
      if (incomingEdges.length > 0) {
        const sourceNode = allNodes.find((n) => n.id === incomingEdges[0].source);
        if (sourceNode &&
            (sourceNode.data.stepType === 'query' ||
             sourceNode.data.stepType === 'queryTableCache')) {
          dataStepId = sourceNode.data.stepId || sourceNode.id;
        }
      }
    }
    if (dataStepId) {
      const dataStepNode = allNodes.find((n) => (n.data.stepId || n.id) === dataStepId);
      if (dataStepNode && dataStepNode.data.systemObject) {
        displayDescription = `Iterate through ${dataStepNode.data.systemObject}`;
      }
    }
  }
  return displayDescription;
}

// Connection handles (shared shape across every step).
function StepHandles({ data, color, isConnectable }) {
  return (
    <>
      <Handle
        type="target"
        position={Position.Top}
        isConnectable={isConnectable}
        style={{ background: color }}
      />
      {data.stepType !== 'ifElse' && (
        <Handle
          type="source"
          position={Position.Bottom}
          id="next"
          isConnectable={isConnectable}
          style={{ background: color }}
        />
      )}
      {data.stepType !== 'start' && (
        <Handle
          type="source"
          position={Position.Right}
          id="error"
          isConnectable={isConnectable}
          style={{ background: '#ef4444', top: '70%' }}
        />
      )}
      {data.stepType === 'ifElse' && (
        <>
          <Handle
            type="source"
            position={Position.Right}
            id="true"
            isConnectable={isConnectable}
            style={{ background: '#10b981', top: '30%' }}
          />
          <Handle
            type="source"
            position={Position.Left}
            id="false"
            isConnectable={isConnectable}
            style={{ background: '#f59e0b', top: '30%' }}
          />
        </>
      )}
    </>
  );
}

// Compact, color-by-connector step node.
function StepNode({ data, isConnectable, allNodes, edges, id }) {
  const { connectorColors, connectorInfo } = useContext(NodeDisplayContext);
  const icon = iconMap[data.stepType] || <FaQuestion />;
  const displayDescription = resolveDescription(data, allNodes, edges, id);

  // Color by connector (distinct per connector); brand orange for non-connector
  // (logic/variable) steps.
  const accent = colorForNode(data, connectorColors);
  const connectorStep = isConnectorStep(data);
  const connectorCode = data.connectorNumber || data.connectorId || null;
  // Rich hover tooltip for the CC pill: connector number, system, description.
  const cInfo = connectorCode ? connectorInfo?.[connectorCode] : null;
  const connectorTitle = [
    `Connector ${cInfo?.connectorNumber || connectorCode || ''}`,
    cInfo?.system && `System: ${cInfo.system}`,
    cInfo?.description,
  ].filter(Boolean).join('\n');
  const verb = verbMap[data.stepType] || 'Step';
  // Bold primary line: the system object when present, else the step label.
  const title = data.systemObject || data.label || verb;
  const fullDesc = displayDescription || '';

  return (
    <div
      className={`step-node-compact${connectorStep ? '' : ' is-generic'}`}
      style={{ '--accent': accent }}
    >
      <span className="wf-accent-bar" />

      {data.stepId && <div className="wf-code">{data.stepId}</div>}

      <div className="wf-icon-tile">
        <span className="wf-icon">{icon}</span>
        <span className="wf-verb">{verb}</span>
      </div>

      <div className="wf-main">
        <div className="wf-title" title={title}>{title}</div>
        {fullDesc && (
          <div className="wf-desc" title={fullDesc}>{truncate(fullDesc, DESC_MAX)}</div>
        )}
      </div>

      {connectorStep && connectorCode && (
        <div className="wf-connector" title={connectorTitle}>
          {connectorCode}
        </div>
      )}

      <StepHandles data={data} color={accent} isConnectable={isConnectable} />
    </div>
  );
}

export default memo(StepNode);
